import React, { useEffect, useState } from "react";
import axios from "../api/client";

export default function DispatchReport() {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);

  // Filters
  const [statusFilter, setStatusFilter] = useState("All");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [shipper, setShipper] = useState("All");
  const [orderNo, setOrderNo] = useState("");

  // Dropdown data
  const [shipperList, setShipperList] = useState([]);
  const [orderList, setOrderList] = useState([]);

  // ---------------- INITIAL LOAD ----------------
  useEffect(() => {
    fetchShippers();
    fetchOrders();
  }, []);

  // ---------------- FETCH SHIPPERS ----------------
  const fetchShippers = async () => {
    try {
      const res = await axios.get("/api/reports/shippers");
      setShipperList(res.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  // ---------------- FETCH ORDERS ----------------
  const fetchOrders = async () => {
    try {
      const res = await axios.get("/api/reports/orders");
      setOrderList(res.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  // ---------------- FETCH DATA ----------------
  const fetchData = async () => {
    try {
      const res = await axios.get("/api/reports/dispatch-status", {
        params: {
          dateFrom,
          dateTo,
          shipper,
          orderNo,
          status: statusFilter
        }
      });

      const safeData = Array.isArray(res.data) ? res.data : [];

      setData(safeData);
      setFilteredData(safeData);

    } catch (err) {
      console.error("API ERROR:", err);
      setData([]);
      setFilteredData([]);
    }
  };

  // ---------------- EXPORT FUNCTION ----------------
  const handleExport = async (type) => {
    try {
      const response = await axios.get(
        `/api/reports/dispatch-status/export/${type}`,
        {
          params: {
            dateFrom,
            dateTo,
            shipper,
            orderNo,
            status: statusFilter
          },
          responseType: "blob"
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");

      let fileName = "Dispatch_Report";
      if (type === "csv") fileName += ".csv";
      if (type === "excel") fileName += ".xlsx";
      if (type === "ppt") fileName += ".pptx";

      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();

    } catch (err) {
      console.error("Export Error:", err);
    }
  };

  // ---------------- STATUS COLOR ----------------
  const getStatusStyle = (status) => {
    switch (status) {
      case "Yet to Assign":
        return { color: "red", fontWeight: "bold" };
      case "Assigned":
        return { color: "orange", fontWeight: "bold" };
      case "Completed":
        return { color: "green", fontWeight: "bold" };
      default:
        return {};
    }
  };

  return (
    <div style={styles.wrapper}>
      <div style={styles.card}>
        <h2 style={styles.title}>Dispatch Status Report</h2>

        {/* FILTERS */}
        <div style={styles.filters}>

          <input type="date" style={styles.input} value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          <input type="date" style={styles.input} value={dateTo} onChange={(e) => setDateTo(e.target.value)} />

          <select style={styles.input} value={shipper} onChange={(e) => setShipper(e.target.value)}>
            <option value="All">All Shippers</option>
            {shipperList.map((s, i) => (
              <option key={i}>{s.ShipperName}</option>
            ))}
          </select>

          <div style={{ display: "flex", gap: "10px" }}>
            <input
              style={styles.input}
              placeholder="Enter Order No"
              value={orderNo}
              onChange={(e) => setOrderNo(e.target.value)}
            />

            <select style={styles.input} onChange={(e) => setOrderNo(e.target.value)}>
              <option value="">Select Order</option>
              {orderList.map((o, i) => (
                <option key={i}>{o.OrderID}</option>
              ))}
            </select>
          </div>

          <select style={styles.input} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="All">All Status</option>
            <option value="Yet to Assign">Yet to Assign</option>
            <option value="Assigned">Assigned</option>
            <option value="Completed">Completed</option>
          </select>

          <button style={styles.button} onClick={fetchData}>Search</button>
        </div>

        {/* EXPORT BUTTONS */}
        <div style={{ display: "flex", gap: "10px", marginBottom: "10px" }}>
          <button style={styles.button} onClick={() => handleExport("csv")}>CSV</button>
          <button style={styles.button} onClick={() => handleExport("excel")}>Excel</button>
          <button style={styles.button} onClick={() => handleExport("ppt")}>PPT</button>
        </div>

        {/* TABLE */}
        <div style={{ overflowX: "auto" }}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th>Order ID</th>
                <th>Order Date</th>
                <th>Shipper</th>
                <th>Total Pieces</th>
                <th>Status</th>
                <th>Assigned To</th>
                <th>Completed By</th>
              </tr>
            </thead>

            <tbody>
              {filteredData.length === 0 ? (
                <tr>
                  <td colSpan="7" style={{ textAlign: "center", padding: "10px" }}>
                    No Data Found
                  </td>
                </tr>
              ) : (
                filteredData.map((row, i) => (
                  <tr key={i}>
                    <td>{row?.OrderID || "-"}</td>
                    <td>{row?.OrderDate ? new Date(row.OrderDate).toLocaleString() : "-"}</td>
                    <td>{row?.ShipperName || "-"}</td>
                    <td>{row?.TotalPieces || 0}</td>
                    <td style={getStatusStyle(row?.Status)}>{row?.Status || "-"}</td>
                    <td>{row?.AssignedTo || "-"}</td>
                    <td>{row?.CompletedBy || "-"}</td>
                  </tr>
                ))
              )}
            </tbody>

          </table>
        </div>
      </div>
    </div>
  );
}

// ---------------- STYLES ----------------
const styles = {
  wrapper: {
    background: "#f3f4f6",
    minHeight: "100vh",
    padding: "20px",
    display: "flex",
    justifyContent: "center",
  },
  card: {
    width: "98%",
    maxWidth: "1100px",
    background: "#fff",
    padding: "20px",
    borderRadius: "12px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
  },
  title: {
    textAlign: "center",
    color: "#C30017",
    marginBottom: "20px",
  },
  filters: {
    display: "flex",
    gap: "10px",
    marginBottom: "15px",
    flexWrap: "wrap",
  },
  input: {
    padding: "10px",
    borderRadius: "6px",
    border: "1px solid #ccc",
    minWidth: "180px",
  },
  button: {
    padding: "10px 20px",
    background: "#C30017",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
  },
};